import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import GridItem from '../../../layout/GridComponents/GridItem';
import RestrictedCompaniesTable from './RestricedCompaniesTable.component';
import {
  useRestrictedListsOptions,
  useRestrictedCompaniesList,
  usePreviousMonthCompaniesList,
} from './services/queries';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

const RestrictedCompanies: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = () => {
  const {
    data: optionsData,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useRestrictedListsOptions();
  const [listOptions, setListOptions] = useState<any>(null);

  // Only display the dropdown when the data has been returned
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(toggleTitleBarLoading(true));
    if (optionsData && optionsData.length) {
      // Handle both formats: [[keys...]] or [keys...]
      const dataKeys = Array.isArray(optionsData[0])
        ? optionsData[0]
        : optionsData;

      if (Array.isArray(dataKeys)) {
        const mappedOptions = dataKeys.map((key: any) => {
          return {
            label: key,
            value: key,
          };
        });

        const fulloptions = [
          { label: 'New this month', value: 'new-this-month' },
          ...mappedOptions,
        ];

        setListOptions(fulloptions);
      }
    } else if (optionsError) {
      // If there is an error then stop the title bar loading
      dispatch(toggleTitleBarLoading(false));
    }
  }, [optionsData, optionsError, dispatch]);

  useEffect(() => {
    if (listOptions) {
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select Sanctions List: ',
          titleBarKey: 'optionSelect',
          values: listOptions,
          displayOnOverviewPage: true,
          aValueIsAlwaysSelected: false,
          currentValue: 'new-this-month',
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
    }

    return () => {
      dispatch(
        removeTitleBarComponent('optionSelect', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [listOptions, dispatch]);

  // Get the currently selected view, defaulting to 'new-this-month' if not set
  const optionSelected = useTitleBarSelect('optionSelect') || 'new-this-month';

  // Get the list of restricted companies
  const {
    data: companiesList,
    isLoading: companiesLoading,
    isError: companiesError,
    error: companiesErrorObj,
  } = useRestrictedCompaniesList(optionSelected);

  const {
    data: previousMonthCompaniesList,
    isLoading: previousMonthLoading,
    isError: previousMonthError,
    error: previousMonthErrorObj,
  } = usePreviousMonthCompaniesList(optionSelected);

  // Determine which data source to use based on selection
  const isNewThisMonth = optionSelected === 'new-this-month';
  const isLoading = isNewThisMonth ? previousMonthLoading : companiesLoading;
  const isError = isNewThisMonth ? previousMonthError : companiesError;
  const error = isNewThisMonth ? previousMonthErrorObj : companiesErrorObj;
  const data = isNewThisMonth ? previousMonthCompaniesList : companiesList;

  // Check if this is a 403 error
  const is403Error =
    (error as any)?.response?.status === 403 ||
    (error as any)?.message?.includes('status code 403');

  // Determine error message based on error type
  const errorMessage = is403Error
    ? 'You are not authorised to access this resource. Please contact RiskSystem support to request access.'
    : 'Unknown error. Please contact RiskSystem support.';

  return (
    <QueryBoundary
      isLoading={isLoading}
      isError={isError}
      data={data}
      loadingMessages={['Loading Restricted Companies Data...']}
      errorMessage={errorMessage}
      noDataMessage="No restricted companies data available."
    >
      {(companiesData) => (
        <GridItem xs={12} card>
          <RestrictedCompaniesTable
            restrictedCompanies={{
              data: [companiesData],
            }}
            selectedOption={optionSelected}
          />
        </GridItem>
      )}
    </QueryBoundary>
  );
};

export default RestrictedCompanies;
